import { twMerge } from 'tailwind-merge';
import type { ComponentOrientation } from '@/types';
import type { TabChild } from './types';

export const classes = {
  verticalWrapper: 'flex',
  labels: 'relative border-black/10 dark:border-white/10',
  horizontal: 'border-b',
  vertical: 'border-r flex flex-col',
  label:
    'py-2.5 px-3 inline-flex items-center text-sm ease-in duration-100 select-none transition-colors whitespace-pre',
  verticalLabel: 'py-2 my-1 pl-2 pr-4',
  bar: 'absolute duration-200 ease-in-out',
  horizontalBar:
    'h-[2px] bottom-[-1px] rounded-full bg-grad transition-[left,width]',
  verticalBar:
    'w-0 right-[-1px] border-r-[3px] border-brand-accent transition-[top,height]',
  activeTab: 'text-brand-accent dark:text-brand-accent-dark font-semibold',
  inactiveTab:
    'text-taupe-2 hover:text-taupe-3 dark:text-mauve-2 dark:hover:text-mauve-1 font-semibold',
  enabled: 'cursor-pointer',
  disabled: 'cursor-not-allowed',
};

export function getMargins(tabs: any[], i: number) {
  if (i === 0) return 'mr-1';
  else if (i === tabs.length - 1) return 'ml-1';
  else return 'mx-1';
}

export function getInitialTab(tabs: TabChild[], defaultTab?: string): string {
  // If an invalid tab name is provided, default to the first tab
  const tabNames = getTabNames(tabs);
  if (!defaultTab || !tabNames.includes(defaultTab)) return tabNames[0];
  return defaultTab;
}

export function getActiveElementSizing(
  blockName: string,
  orientation: ComponentOrientation
) {
  const el: HTMLDivElement = document.querySelector(
    `.${blockName}-active-tab`
  )!;
  if (!el) return null;
  return orientation === 'horizontal'
    ? {
        left: `${el.offsetLeft}px`,
        width: `${el.clientWidth}px`,
      }
    : {
        top: `${el.offsetTop}px`,
        height: `${el.clientHeight}px`,
      };
}

export function getTabClasses(
  tab: TabChild,
  index: number,
  children: TabChild[],
  name: string,
  activeTab: string,
  orientation: ComponentOrientation
) {
  return twMerge(
    classes.label,
    orientation === 'vertical' && classes.verticalLabel,
    orientation === 'horizontal' && getMargins(children, index),
    tab.props.disabled ? classes.disabled : classes.enabled,
    activeTab === tab.props.name
      ? `${classes.activeTab} ${name}-active-tab`
      : classes.inactiveTab
  );
}

export function isTabChanged(
  tabs: TabChild[],
  activeTab: string,
  defaultTab: string
) {
  return activeTab !== defaultTab && getTabNames(tabs).includes(defaultTab);
}

function getTabNames(tabs: TabChild[]) {
  return tabs.map((tab) => tab.props.name);
}
